import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Note } from '@mui/icons-material';

import { ContentStack, EditorContainer, MessageContainer } from '../EmailItem/styled';
import { useImagePreview } from '../EmailItem/hooks/useImagePreview';
import { MessageProps } from '../../types';
import { Timeline } from '../Timeline';
import AttachmentsList from '../../AttachmentsList';
import { ImagePreviewDialog } from '../ImagePreviewDialog';

export const NoteItem = forwardRef<HTMLDivElement, MessageProps>((props, ref) => {
  const { t } = useTranslation();
  const { handleEditorClickEvent, previewImage, handleClosePreview } = useImagePreview();
  const theme = useTheme();
  const authorName =
    `${props.history.authorFirstName || ''} ${props.history.authorLastName || ''}`.trim() || props.history.emailTo;

  const createdAtTime = dayjs(props.history.createdAt).format('HH:mm');

  return (
    <MessageContainer ref={ref} sx={{ justifyContent: props.isCurrentUser ? 'flex-end' : 'flex-start' }}>
      <Stack
        order={props.isCurrentUser ? 1 : 2}
        mr={(theme) => theme.spacing(props.isCurrentUser ? 2.5 : 0)}
        ml={(theme) => theme.spacing(props.isCurrentUser ? 0 : 2.5)}
      >
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent={props.isCurrentUser ? 'flex-end' : 'flex-start'}
          alignItems="center"
          pb={1}
          gap={2}
        >
          <Box gap={1} display="flex">
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="subtitle2">{authorName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cases.view.content.noteText')}
              </Typography>
            </Box>
          </Box>
        </Stack>
        <Stack
          maxWidth={600}
          minWidth={300}
          borderRadius="16px"
          border={(theme) => `1px solid ${theme.palette.divider}`}
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
        >
          <Box
            display="flex"
            padding={(theme) => theme.spacing(1, 2)}
            flexDirection="row"
            borderRadius="16px 16px 0 0"
            alignItems="center"
            gap={1}
            bgcolor={(theme) => theme.palette.secondary.light}
          >
            <Note sx={{ color: 'text.secondary' }} />
            <Typography color="text.secondary" variant="subtitle2">
              {t('cases.view.content.note')}
            </Typography>
          </Box>
          <Stack borderRadius="0 0 16px 16px" bgcolor={(theme) => theme.palette.background.paper} padding={2}>
            <ContentStack>
              <EditorContainer
                onClick={(e) => {
                  handleEditorClickEvent(e.target as HTMLElement);
                }}
                dangerouslySetInnerHTML={{ __html: String(props.history.noteContent) }}
              />
              <AttachmentsList files={props.history.files} />
            </ContentStack>
          </Stack>
        </Stack>
        <Typography textAlign="right" pt={1} color="text.disabled" variant="subtitle2">
          {createdAtTime}
        </Typography>
      </Stack>

      <Timeline
        isCurrentUser={props.isCurrentUser}
        icon={Note}
        color="text.secondary"
        background={theme.palette.secondary.light}
      />
      <ImagePreviewDialog previewImage={previewImage} onClose={handleClosePreview} />
    </MessageContainer>
  );
});

NoteItem.displayName = 'NoteItem';
